import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import InputGroups from "@/UI/components/InputGroups";
import ConfirmPopup, { type ConfirmPopupDetails } from "@/UI/popups/ConfirmPopup";
import { TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

interface LangValue {
    lang: string;
    value: string;
    default?: boolean;
}

interface InitialCategoryData {
    _id?: string;
    name: LangValue[];
    description: LangValue[];
    picture: string | null;
    sort: number;
}

export interface CategoriesFormDetails {
    categoryId?: string;
    initialData?: InitialCategoryData;
    isDuplicate?: boolean;
    onSuccess: () => void;
}

interface FormState {
    nameAr: string;
    nameEn: string;
    descriptionAr: string;
    descriptionEn: string;
    sort: string;
    existingPicture: string | null;
    newPicture: File | null;
}

// ── MUI Shared Styles ─────────────────────────────────────────────────────────

const getTextFieldSx = (dir: "rtl" | "ltr") => ({
    direction: dir,
    "& .MuiInputLabel-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-gray-02)",
        transformOrigin: dir === "rtl" ? "top right" : "top left",
        right: dir === "rtl" ? 0 : "unset",
        left: dir === "rtl" ? "unset" : 0,
        "&:not(.MuiInputLabel-shrink)": {
            transform: dir === "rtl" ? "translate(-14px, 14px) scale(1)" : "translate(14px, 14px) scale(1)",
        },
        "&.MuiInputLabel-shrink": {
            transform: dir === "rtl" ? "translate(-14px, -9px) scale(0.75)" : "translate(14px, -9px) scale(0.75)",
        },
        "&.Mui-focused": {
            color: "var(--color-orange)",
        },
    },
    "& .MuiOutlinedInput-root": {
        fontFamily: "Alexandria, sans-serif",
        fontSize: "13.5px",
        color: "var(--color-dark)",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-lev3)",
        "& fieldset": {
            borderColor: "var(--color-gray-01)",
            borderWidth: "1.5px",
        },
        "& fieldset legend": {
            textAlign: dir === "rtl" ? "right" : "left",
            marginLeft: dir === "rtl" ? "auto" : undefined,
            marginRight: dir === "rtl" ? "0px" : undefined,
        },
        "&:hover fieldset": {
            borderColor: "var(--color-gray-02)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "var(--color-orange)",
            borderWidth: "1.5px",
        },
    },
    "& .MuiOutlinedInput-input": {
        fontFamily: "Alexandria, sans-serif",
        padding: "14px",
        textAlign: dir === "rtl" ? "right" : "left",
    },
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function getLangValue(arr: LangValue[] | undefined, lang: string): string {
    if (!arr || !Array.isArray(arr)) return "";
    return arr.find((item) => item.lang === lang)?.value ?? "";
}

function buildInitialForm(initial: InitialCategoryData | undefined, isDuplicate?: boolean): FormState {
    if (!initial) {
        return {
            nameAr: "",
            nameEn: "",
            descriptionAr: "",
            descriptionEn: "",
            sort: "0",
            existingPicture: null,
            newPicture: null,
        };
    }

    return {
        nameAr: getLangValue(initial.name, "ar"),
        nameEn: getLangValue(initial.name, "en"),
        descriptionAr: getLangValue(initial.description, "ar"),
        descriptionEn: getLangValue(initial.description, "en"),
        sort: initial.sort?.toString() ?? "0",
        existingPicture: isDuplicate ? null : (initial.picture ?? null),
        newPicture: null,
    };
}

function buildFormData(form: FormState): FormData {
    const fd = new FormData();

    const name = [
        ...(form.nameAr.trim() ? [{ lang: "ar", value: form.nameAr.trim(), default: true }] : []),
        ...(form.nameEn.trim() ? [{ lang: "en", value: form.nameEn.trim(), default: false }] : []),
    ];

    const description = [
        ...(form.descriptionAr.trim() ? [{ lang: "ar", value: form.descriptionAr.trim(), default: true }] : []),
        ...(form.descriptionEn.trim() ? [{ lang: "en", value: form.descriptionEn.trim(), default: false }] : []),
    ];

    fd.append("name", JSON.stringify(name));
    fd.append("description", JSON.stringify(description));
    fd.append("sort", form.sort || "0");

    // Existing picture (kept URL)
    if (form.existingPicture) {
        fd.append("picture", form.existingPicture);
    }

    // New picture (file)
    if (form.newPicture) {
        fd.append("newPicture", form.newPicture);
    }

    return fd;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
    onClose: () => void;
    details?: CategoriesFormDetails;
}

export default function CategoriesForm({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();

    const categoryId = details?.categoryId;
    const isDuplicate = details?.isDuplicate;
    const isEdit = !!categoryId && !isDuplicate;

    const [form, setForm] = useState<FormState>(() => buildInitialForm(details?.initialData, isDuplicate));

    const pictureInputRef = useRef<HTMLInputElement>(null);

    // ── Helpers ───────────────────────────────────────────────────────────────

    const showError = (message: string) => {
        const name = "category-form-error";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message,
                variant: "danger",
                confirmLabel: t.categoryForm.ok,
                onConfirm: () => {},
            } as ConfirmPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    // ── Picture ───────────────────────────────────────────────────────────────

    const handleAddPictureClick = () => {
        pictureInputRef.current?.click();
    };

    const handlePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setForm((prev) => ({
                ...prev,
                newPicture: file,
                existingPicture: null,
            }));
        }
        e.target.value = "";
    };

    const handleRemovePicture = () => {
        setForm((prev) => ({
            ...prev,
            existingPicture: null,
            newPicture: null,
        }));
    };

    const currentPictureUrl = form.newPicture ? URL.createObjectURL(form.newPicture) : form.existingPicture;

    // ── Submit fetch ──────────────────────────────────────────────────────────

    const { loading: submitting, error: submitError, status: submitStatus, invoke: submitInvoke } = useFetch("products/categories", {}, false);

    const wasSubmitting = useRef(false);
    useEffect(() => {
        if (submitting) {
            wasSubmitting.current = true;
        } else if (wasSubmitting.current) {
            wasSubmitting.current = false;
            if (submitStatus === 201 || submitStatus === 200) {
                details?.onSuccess?.();
                onClose();
            } else if (submitError) {
                showError(submitError);
            }
        }
    }, [submitting]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.nameAr.trim() && !form.nameEn.trim()) {
            showError(t.categoryForm.nameRequired);
            return;
        }

        const formData = buildFormData(form);

        submitInvoke({
            method: isEdit ? "PUT" : "POST",
            ...(isEdit ? { param: categoryId } : {}),
            formData,
        });
    };

    // ── Derived ────────────────────────────────────────────────────────────────

    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = getTextFieldSx(dir as "rtl" | "ltr");
    const textFieldSxRtl = getTextFieldSx("rtl");
    const textFieldSxLtr = getTextFieldSx("ltr");

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <form className="categories-form" onSubmit={handleSubmit} noValidate>
            <div className="categories-form-fields">
                {/* ── Name Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.categoryForm.name}>
                    <div className="cf-row cf-row--2">
                        <TextField
                            fullWidth
                            label={t.categoryForm.arabic}
                            value={form.nameAr}
                            onChange={(e) => setField("nameAr", e.target.value)}
                            sx={textFieldSxRtl}
                        />
                        <TextField
                            fullWidth
                            label={t.categoryForm.english}
                            value={form.nameEn}
                            onChange={(e) => setField("nameEn", e.target.value)}
                            sx={textFieldSxLtr}
                        />
                    </div>
                </InputGroups>

                {/* ── Description Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.categoryForm.description}>
                    <div className="cf-row cf-row--2">
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={t.categoryForm.arabic}
                            value={form.descriptionAr}
                            onChange={(e) => setField("descriptionAr", e.target.value)}
                            sx={textFieldSxRtl}
                        />
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={t.categoryForm.english}
                            value={form.descriptionEn}
                            onChange={(e) => setField("descriptionEn", e.target.value)}
                            sx={textFieldSxLtr}
                        />
                    </div>
                </InputGroups>

                {/* ── Sort Section ── */}
                <InputGroups icon="/icons/inputs-group-icon.svg" title={t.categoryForm.sort}>
                    <TextField
                        fullWidth
                        type="number"
                        label={t.categoryForm.sort}
                        value={form.sort}
                        onChange={(e) => setField("sort", e.target.value)}
                        slotProps={{ htmlInput: { min: 0 } }}
                        sx={textFieldSx}
                    />
                </InputGroups>

                {/* ── Picture Section ── */}
                <InputGroups
                    icon="/icons/inputs-group-icon.svg"
                    title={t.categoryForm.pictureSection}
                    addButton={{
                        label: t.categoryForm.addPicture,
                        onClick: handleAddPictureClick,
                    }}
                >
                    <input ref={pictureInputRef} type="file" accept="image/*" onChange={handlePictureChange} className="cf-hidden-input" />
                    {currentPictureUrl ? (
                        <div className="cf-picture-preview">
                            <img src={currentPictureUrl} alt="" className="cf-picture-image" />
                            <button type="button" className="cf-picture-delete" onClick={handleRemovePicture}>
                                <img src="/icons/delete-icon.svg" alt="" width={14} height={14} />
                            </button>
                        </div>
                    ) : (
                        <div className="cf-empty">{t.table.empty}</div>
                    )}
                </InputGroups>
            </div>

            {/* ── Footer ── */}
            <div className="cf-footer">
                <button type="button" className="cf-btn cf-btn--cancel" onClick={onClose} disabled={submitting}>
                    {t.categoryForm.cancel}
                </button>
                <button type="submit" className="cf-btn cf-btn--submit" disabled={submitting}>
                    {submitting ? <span className="cf-spinner" /> : isEdit ? t.categoryForm.update : t.categoryForm.create}
                </button>
            </div>
        </form>
    );
}
